interface IssueLike {
  fields?: { duedate?: string | null };
  mappedFields?: { intervention_datetime?: string | null };
}

// Scheduled-visit date: Jira's native duedate, or intervention_datetime for the
// desks that use their own field instead (LLF / Flagship).
export const getInterventionDate = (issue?: IssueLike): string | null => {
  const value = issue?.fields?.duedate || issue?.mappedFields?.intervention_datetime;
  return typeof value === 'string' && value ? value : null;
};

// Same date as a local calendar day, for day-granularity comparisons. Sliced, so a
// datetime is the day as written (Paris) rather than projected on the viewer's zone.
export const getInterventionDay = (issue?: IssueLike): Date | null => {
  const value = getInterventionDate(issue);
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

/** Today in Europe/Paris as `YYYY-MM-DD` — the business day, whatever the viewer's own zone. */
export const parisToday = (): string =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });

/**
 * `value` as a real `YYYY-MM-DD` calendar day, or null when it is not one. A datetime is sliced to
 * its day, as written, so a Jira field carrying an hour still yields the Paris day.
 * ⚠️ `new Date('2026-02-31')` does not throw — it rolls over to 3 March — so the round trip is
 * what proves the day exists.
 */
export const calendarDay = (value: string | null | undefined): string | null => {
  const day = String(value ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const parsed = new Date(`${day}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : null;
};

/** Why a typed day cannot be an intervention date. `empty` carries no sentence of its own. */
export type DayRefusal = 'empty' | 'malformed' | 'past';

/**
 * Whether a `YYYY-MM-DD` day can be saved as an intervention date, and why not when it cannot.
 *
 * Mirrors `interventionDate` in the backend schema: a string comparison against today in Paris,
 * at day granularity, so today is accepted and the two sides never disagree on what today is.
 * ⚠️ `new Date('2026-02-31')` does not throw — it rolls over to 3 March — so the round trip is
 * what proves the day exists. A native date input rarely emits one; the helper does not trust it.
 * @param value - The day as typed, `YYYY-MM-DD`.
 * @param today - Today in Paris, injectable for the tests.
 * @returns The refusal, or `null` when the day is schedulable.
 */
export const interventionDayRefusal = (
  value: string,
  today: string = parisToday()
): DayRefusal | null => {
  if (!value) return 'empty';
  // `!== value` and not merely null: a typed datetime is a day `calendarDay` would happily slice,
  // and this field is a day picker — what it emits must be a day already.
  const day = calendarDay(value);
  if (day === null || day !== value) return 'malformed';
  return day < today ? 'past' : null;
};
